// 로켓등록 목록용(_light) 문서 다루기 — 의존성 없는 순수 함수(시험: __tests__/rocketRegLight.test.mjs)
// 목록 조회(view:'list')는 무거운 칸을 뺀 문서를 준다. 그걸 그대로 저장하면 빠진 칸이 지워진다 → 저장 전에 막는다.

export const LIGHT_SAVE_ERROR = '목록용 문서는 저장할 수 없습니다(완전 문서를 먼저 불러오세요)';
export const FULL_CHUNK_SIZE = 20; // 서버 getFull 한 번에 최대 20개

// 문서 또는 옵션 하나라도 목록용이면 참
export function isLightDoc(p) {
  if (!p || typeof p !== 'object') return false;
  if (p._light) return true;
  return Array.isArray(p.options) && p.options.some(o => o && o._light);
}

export function assertNotLight(p) {
  if (isLightDoc(p)) throw new Error(LIGHT_SAVE_ERROR);
}

// 부분 저장 — updateFields 값 1단계와 그 배열 원소를 본다
export function assertUpdateFieldsNotLight(updateFields) {
  if (!updateFields || typeof updateFields !== 'object') return;
  Object.keys(updateFields).forEach(key => {
    const v = updateFields[key];
    if (Array.isArray(v)) v.forEach(assertNotLight);
    else assertNotLight(v);
  });
}

// 받을 id 고르기 — 요청 ids 중 store 에 없거나 목록용인 것(중복 없이, 문자열)
export function pickLightIds(products, ids) {
  const want = uniqueIds(ids);
  const byId = new Map((products || []).map(p => [String(p && p._id), p]));
  return want.filter(id => !byId.has(id) || isLightDoc(byId.get(id)));
}

// 목록 재조회가 덮어쓸 칸 — 목록 칸 중 스칼라 최상위·옵션 칸만(객체 칸 seo·coupangDoc·processError·rocketRegInfo 는 완전 문서 것 유지)
export const LIST_STATUS_FIELDS = ['seoStatus', 'pricingStatus', 'mainImageStatus', 'additionalImageStatus', 'detailImageStatus', 'sizeChartStatus', 'koreanLabelStatus', 'quotationStatus'];
export const LIST_TOP_FIELDS = [...LIST_STATUS_FIELDS, 'registered', 'verified', 'errorDetails', 'detailImageWarning', 'productName', 'productNameTrans',
  'productImage', 'productFeature', 'targetKeyword', '구매링크', '_sizeChartRequired'];
export const LIST_OPTION_FIELDS = [...LIST_STATUS_FIELDS, 'registered', 'registrationStatus', 'verified', 'errorDetails'];

function copyFields(dst, src, fields) {
  fields.forEach(k => { if (k in src) dst[k] = src[k]; });
}

// 완전 문서 + 같은 _id 의 목록용 문서 → 완전 문서(새 객체)에 목록 칸만 새 값으로. _light 표식은 안 붙인다.
// 옵션은 optionNumber 일치(둘 다 있으면), 아니면 같은 인덱스 옵션에 덮는다.
export function mergeListIntoFull(full, light) {
  if (!full || !light) return full;
  const out = { ...full };
  copyFields(out, light, LIST_TOP_FIELDS);
  if (Array.isArray(full.options) && Array.isArray(light.options)) {
    out.options = full.options.map((opt, i) => {
      if (!opt || typeof opt !== 'object') return opt;
      const hasNum = opt.optionNumber !== undefined && opt.optionNumber !== null;
      const atIndex = light.options[i];
      const src = hasNum
        ? (light.options.find(o => o && o.optionNumber === opt.optionNumber) ||
          (atIndex && (atIndex.optionNumber === undefined || atIndex.optionNumber === null) ? atIndex : null))
        : atIndex;
      if (!src) return opt;
      const merged = { ...opt };
      copyFields(merged, src, LIST_OPTION_FIELDS);
      return merged;
    });
  }
  return out;
}

export function uniqueIds(ids) {
  const out = [];
  (ids || []).forEach(id => {
    if (id === undefined || id === null || id === '') return;
    const s = String(id);
    if (!out.includes(s)) out.push(s);
  });
  return out;
}

export function chunkIds(ids, size = FULL_CHUNK_SIZE) {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

// getFull 응답 검사 — 실패·빠진 id 가 있으면 throw, 아니면 완전 문서 배열
export function checkFullResponse(data, requestedIds) {
  const d = data || {};
  if (d['요청결과'] !== 'ok' || !Array.isArray(d.products)) {
    throw new Error(d.error || '상품 정보를 불러오지 못했습니다. 잠시 뒤 다시 시도해주세요.');
  }
  const got = new Set(d.products.map(p => String(p && p._id)));
  const missing = uniqueIds([...(d.missing || []), ...(requestedIds || []).filter(id => !got.has(String(id)))]);
  if (missing.length) {
    throw new Error(`상품 정보를 찾지 못했습니다(${missing.length}건): ${missing.join(', ')}`);
  }
  return d.products;
}

// 요청 ids 순서대로 store 문서 고르기(없는 id 는 뺀다)
export function pickByIds(products, ids) {
  const byId = new Map((products || []).map(p => [String(p && p._id), p]));
  return uniqueIds(ids).map(id => byId.get(id)).filter(Boolean);
}

// 상세이미지 원본 칸 — 완전 문서에만 의미가 있다(searchRocketProducts·cliPageLoadMissingProducts 와 같은 꼴)
export function addOriginDetailImages(product) {
  if (!product) return product;
  product.originDetailImages = (product.detailImages || []).map(img => {
    const url = typeof img === 'string' ? img : (img.img || img.url || '');
    return { originalUrl: url, img: url };
  });
  return product;
}
